import { booleanAttribute, ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { ZagButton } from '../button/button';
import { ZagIcon } from '../icon/icon';

export type AlertSeverity = 'info' | 'warning' | 'error' | 'success';

export type AlertRole = 'status' | 'alert';

const SEVERITY_ICONS: Record<AlertSeverity, string> = {
  info: 'info',
  warning: 'warning',
  error: 'error',
  success: 'check-circle',
};

// Slots: [slot=action]
@Component({
  selector: 'zag-alert',
  imports: [ZagButton, ZagIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './alert.component.scss',
  host: {
    '[class]': '"zag-alert zag-alert--" + severity()',
    '[class.zag-alert--compact]': 'compact()',
    '[attr.role]': 'role()',
  },
  template: `
    <span class="zag-alert__icon" aria-hidden="true" [zagIcon]="iconName()"></span>
    <div class="zag-alert__body">
      <div class="zag-alert__title">{{ heading() }}</div>
      @if (description()) {
        <div class="zag-alert__description">{{ description() }}</div>
      }
    </div>
    <div class="zag-alert__action"><ng-content select="[slot=action]" /></div>
    @if (dismissLabel()) {
      <button
        type="button"
        zagButton="icon"
        zagType="secondary"
        zagSize="sm"
        class="zag-alert__dismiss"
        [attr.aria-label]="dismissLabel()"
        (click)="dismissed.emit()"
      >
        <span zagIcon="close"></span>
      </button>
    }
  `,
})
export class AlertComponent {
  readonly severity = input<AlertSeverity>('info');

  readonly heading = input.required<string>();

  readonly description = input<string>();

  readonly icon = input<string>();

  readonly compact = input(false, { transform: booleanAttribute });

  readonly role = input<AlertRole>();

  readonly dismissLabel = input<string>();

  readonly dismissed = output<void>();

  protected readonly iconName = computed(() => this.icon() ?? SEVERITY_ICONS[this.severity()]);
}
